// SPDX-License-Identifier: AGPL-3.0-or-later

import {readRawStorageItem, writeRawStorageItem} from '@app/features/platform/state/PrebootMirror';
import {getProtectedLocalStorage} from '@app/features/platform/state/ProtectedWebStorage';

function getCookie(name: string): string | null {
	if (typeof document === 'undefined') return null;
	const match = document.cookie.match(new RegExp('(?:^|; )' + encodeURIComponent(name) + '=([^;]*)'));
	return match ? decodeURIComponent(match[1]) : null;
}

function setCookie(name: string, value: string, days = 365): void {
	if (typeof document === 'undefined') return;
	const expires = new Date(Date.now() + days * 864e5).toUTCString();
	document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
}

function deleteCookie(name: string): void {
	if (typeof document === 'undefined') return;
	document.cookie = `${encodeURIComponent(name)}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Lax`;
}

export function getSafeItem(key: string): string | null {
	// 1. Try PrebootMirror raw storage (Fluxer's protected storage binding)
	try {
		const val = readRawStorageItem(key);
		if (val !== null && val !== undefined) return val;
	} catch {}

	// 2. Try getProtectedLocalStorage
	try {
		const storage = getProtectedLocalStorage();
		if (storage) {
			const val = storage.getItem(key);
			if (val !== null && val !== undefined) return val;
		}
	} catch {}

	// 3. Try standard window.localStorage (if not deleted by sandbox)
	try {
		if (typeof window !== 'undefined' && 'localStorage' in window && window.localStorage) {
			const val = window.localStorage.getItem(key);
			if (val !== null && val !== undefined) return val;
		}
	} catch {}

	// 4. Try Cookie backup
	try {
		const val = getCookie(key);
		if (val !== null && val !== undefined) return val;
	} catch {}

	return null;
}

export function setSafeItem(key: string, value: string): void {
	// 1. Write PrebootMirror raw storage
	try {
		writeRawStorageItem(key, value);
	} catch {}

	// 2. Write getProtectedLocalStorage
	try {
		const storage = getProtectedLocalStorage();
		if (storage) {
			storage.setItem(key, value);
		}
	} catch {}

	// 3. Write window.localStorage
	try {
		if (typeof window !== 'undefined' && 'localStorage' in window && window.localStorage) {
			window.localStorage.setItem(key, value);
		}
	} catch {}

	// 4. Cookie fallback (if under 3.5KB)
	try {
		if (value.length < 3500) {
			setCookie(key, value);
		}
	} catch {}
}

export function removeSafeItem(key: string): void {
	try {
		writeRawStorageItem(key, null);
	} catch {}

	try {
		const storage = getProtectedLocalStorage();
		if (storage) {
			storage.removeItem(key);
		}
	} catch {}

	try {
		if (typeof window !== 'undefined' && 'localStorage' in window && window.localStorage) {
			window.localStorage.removeItem(key);
		}
	} catch {}

	try {
		deleteCookie(key);
	} catch {}
}
